import { CmsDisplayTextLabel, VistaMultiAttributePicker } from '../../cmsTypes'

export class DateShowtimePickerPropsApi {
    // The attributes that show on the legend
    filteredAttributes: VistaMultiAttributePicker

    /**
     * Toggle to show/hide attribute legend modal
     */
    showAttributeLegend: boolean

    /**
     * Determines whether or not the all films toggle is shown.
     */
    allFilmsToggleShow: boolean
    /**
     * Determines whether the toggle is turned on or off by default.
     */
    allFilmsToggleInitialValue: boolean

    /**
     * Label displayed when there are no showtimes for a site on the selected date.
     * There are however showtimes for dates after the selected date.
     */
    nextShowtimeForSiteOnDateMessage: CmsDisplayTextLabel[] = [
        { text: 'This movie is not available on this date. Next screening is on the ' },
        { text: '${nextScreeningDate|d|Do of MMMM}' },
        { text: '.' },
    ]

    /**
     * Label displayed when there are no showtimes for a site on the selected date.
     * There are however showtimes for dates before the selected date.
     */
    lastShowtimeForSiteOnDateMessage: CmsDisplayTextLabel[] = [
        { text: 'This movie is not available on this date. Last screening is on the ' },
        { text: '${lastScreeningDate|d|Do of MMMM}' },
        { text: '.' },
    ]
    /**
     * Label displayed on all films toggle.
     */
    allFilmsToggleLabel: string = 'Show all films'
    /**
     * The format for the advance booking period notification shown if the selected showtime is not available until a later date.
     */
    showtimeUnavailableAdvanceBookingPeriodNotificationFormat: string =
        'Coming soon. Tickets on sale at ${siteName} from ${startsAt|d|Do of MMMM, h:mm A}. Please come back later.'
    /**
     * The format for the advance booking period notification shown if the selected showtime is not available until later today.
     */
    showtimeUnavailableAdvanceBookingPeriodTodayNotificationFormat: string =
        'Coming soon. Tickets on sale at ${siteName} today from ${startsAt|d|h:mm A}. Please come back later.'
    /**
     * Label displayed at the front of the list of attributes that all showtimes in a group have in common.
     */
    commonAttributesListLabel: string = 'All Showtimes'
    /**
     * Label displayed for the attribute legend.
     */
    attributeLegendLabel: string = 'Showtime Attributes'
    /**
     * Label displayed on the button which opens a modal that shows the attribute legend.
     */
    attributeLegendViewButtonLabel: string = 'Attribute Info'
    /**
     * The help text displayed on the attribute legend modal.
     */
    attributeLegendModalHelpText: string = ''
    /**
     * Label displayed for the runtime of the film.
     */
    runtimeLabel: string = 'Runtime'
    /**
     * Label displayed when no sites have been selected to display showtimes for.
     */
    noSitesSelectedTitle: string = 'No cinemas selected.'
    /**
     * Label displayed below `noSitesSelectedLabel` prompting the user to select sites.
     */
    noSitesSelectedMessage: string = 'Where would you like to see the movie?'
    /**
     * Label displayed when there are no showtimes for the selected filter options.
     */
    noShowtimesForFilterMessage: string = 'There are no showtimes that match the selected filters.'
    /**
     * Label that appears on the 'clear filter' button when the there are no showtimes for the selected filter options.
     */
    noShowtimesForFilterClearButtonLabel: string = 'Clear filters'
    /**
     * Format for message displayed when there are no showtimes at any of the specified sites for a particular film on the selected date.
     * Must include the placeholder '${selected-film-title}' which will be populated with the name of the selected film.
     */
    noShowtimesForFilmMessage: string =
        "There are no showtimes on this date for '${selectedFilmTitle}' at your selected cinemas.\nPlease select another cinema."
    /**
     * Label displayed when there are no showtimes at any of the specified sites for any film on the selected date.
     */
    noShowtimesGenericMessage: string =
        'There are no showtimes on this date at your selected cinemas.\nPlease select another cinema.'
    /**
     * Label displayed when there are no showtimes for a site and film.
     */
    noShowtimesForSiteAndFilmMessage: string = 'This movie is no longer available at this location.'

    /**
     * Label displayed on action button displayed at the bottom of the component.
     */
    actionButtonLabel?: string = 'Add cinemas'
    /**
     * Label for title displayed when no showtimes are available to display for any date.
     */
    noShowtimesTitle: string = 'No showtimes available.'
    /**
     * Label for message displayed when no showtimes are available to display for any date.
     */
    noShowtimesForAnyDateMessage: string = 'Please try another selection.'
    /**
     * The format for the advance booking period notification shown if the film is not available at a particular site until a later date.
     */

    advanceBookingPeriodForSiteNotificationFormat: string =
        'Tickets go on sale at ${siteName} on ${startsAt|d|MMMM Do [at] h:mm A}.'
    /**
     * The format shown the advance booking period notification shown if the film is not available at a particular site until later today.
     */
    advanceBookingPeriodForSiteTodayNotificationFormat: string =
        'Tickets go on sale at ${siteName} today from ${startsAt|d|h:mm A}.'
    /**
     * Title displayed when data failed to load from the API.
     */
    failedToLoadDataTitle: string = 'Sorry, something went wrong.'
    /**
     * Message displayed when data failed to load from the API.
     */
    failedToLoadDataMessage: string = 'Refresh your browser to try loading your selections again.'
    /**
     * Text displayed for the showtime picker film action link.
     */
    filmActionLinkText: string = 'Show full details'
    /**
     * The optional label shown on the play trailer button.
     */
    playTrailerButtonLabel?: string = 'Play trailer'
    /**
     * Label text for the trailer video player error message title.
     */
    failedToLoadTrailerTitle: string = 'Sorry, something went wrong.'
    /**
     * Label text for the trailer video player error reload button.
     */
    failedToLoadTrailerRetryButtonLabel: string = 'Retry'

    /**
     * Display text for the date pickers today label.
     */
    datePickerTodayDateFormat: string = 'Today'
    /**
     * The time format string for showtimes, excluding token for am/pm designator.
     */
    showtimeTimeFormat: string = 'h:mm'
    /**
     * The format string for the showtime's am/pm designator. Leave blank to hide am/pm.
     */
    showtimeAmPmFormat: string = 'A'
    /**
     * The format string for the screening date label of the showtimes.
     */
    businessDateFormat: string = '${businessDate|d|dddd D MMMM}'

    /**
     * [DEPRECATED] Not used anymore.
     * Label displayed when there are no showtimes for a site on the selected date.
     * There are however showtimes for dates after the selected date, but tickets are not available until a later date due to an advance booking rule.
     */
    nextShowtimeForSiteOnDateWithAdvanceBookingPeriodMessage: CmsDisplayTextLabel[] = [
        { text: 'This movie is not available on this date. Next screening is on the ' },
        { text: '${nextScreeningDate|d|Do of MMMM}' },
        { text: '. Tickets on sale from ${bookingPeriodStartsAt|d|Do of MMMM, h:mm A}.' },
    ]
    /**
     * [DEPRECATED] Not used anymore.
     * Label displayed when there are no showtimes for a site on the selected date.
     * There are however showtimes for dates after the selected date, but tickets are not available until later today due to an advance booking rule.
     */
    nextShowtimeForSiteOnDateWithAdvanceBookingPeriodTodayMessage: CmsDisplayTextLabel[] = [
        { text: 'This movie is not available on this date. Next screening is on the ' },
        { text: '${nextScreeningDate|d|Do of MMMM}' },
        { text: '. Tickets on sale today from ${bookingPeriodStartsAt|d|h:mm A}.' },
    ]
    /**
     * [DEPRECATED] Not used anymore.
     * Label displayed when there are no showtimes for a site and film that has a future advance booking period.
     */
    noShowtimesForSiteAndFilmWithAdvanceBookingPeriodMessage: string =
        'This movie is not currently available at this location. Tickets on sale from ${bookingPeriodStartsAt|d|Do of MMMM, h:mm A}.'
}
